/**
 * Rendering de MÍDIA nas bolhas do chat staff (v8 Etapa 3b): imagem/sticker
 * inline (clique abre em nova aba), áudio/vídeo com controls, documento como
 * card de arquivo com download, e os estados "baixando mídia…" (media_pull
 * ainda backfillando — sempre com o fallback textual da mensagem visível) e
 * "Mídia indisponível". Os elementos usam a URL presignada resolvida por
 * useChatMedia direto como src/href (browser faz streaming + cache; sem
 * fetch/blob). Inclui o linkify XSS-safe de URLs http(s) no texto (usado
 * também na mensagem de localização).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Download, FileText } from 'lucide-react';
import { WA_URL_RE, type ChatMessage } from '@erp/shared';
import { Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { formatFileSize } from '@/features/knowledge/knowledge-format';
import { resyncChatMessages } from './chats-queries';
import { mediaAwaitDeadline, mediaAwaitState } from './media-await';
import { downloadChatMedia, useChatMedia } from './use-chat-media';

// o predicado mora no módulo puro da espera (testado pela API); as telas
// seguem importando daqui
export { isMediaContent } from './media-await';

// ── Linkify (XSS-safe: só JSX, nunca innerHTML; só http/https) ──────────────

// a MESMA regex que a formatação do WhatsApp respeita (whatsapp-format.ts, no
// @erp/shared): o que é link aqui nunca vira itálico lá
const URL_RE = WA_URL_RE;

export function LinkifiedText({ text }: { text: string }) {
  const parts = text.split(URL_RE);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) =>
        /^https?:\/\//.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 break-all hover:opacity-80"
          >
            {part}
          </a>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

// ── Estados auxiliares ──────────────────────────────────────────────────────

/**
 * Mídia ainda não copiada para o storage do ERP (media_pull em curso). Mostra
 * SEMPRE o fallback textual da mensagem (caption ?? filename ?? '[imagem]'
 * etc., já materializado em `text` pelo servidor) — se a cópia nunca vier, a
 * bolha continua legível — e, ao lado, o estado da espera (media-await):
 *
 * - dentro do teto, "baixando mídia…": quem a faz sair é a conversa (o evento
 *   chat.message.updated, ou a reconsulta curta de useChatMessages /
 *   useTicketTimeline), que troca esta bolha pelo player;
 * - vencido o teto, "Mídia indisponível" com "Tentar de novo", que reconsulta
 *   como o evento faria. O relógio daqui troca a dica NA HORA do teto, mesmo
 *   que nenhuma resposta nova re-renderize a bolha.
 */
function MediaPending({ message }: { message: ChatMessage }) {
  const qc = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  const [retrying, setRetrying] = useState(false);
  const state = mediaAwaitState(message, now);
  const deadline = mediaAwaitDeadline(message);
  useEffect(() => {
    if (state !== 'awaiting') return;
    const timer = setTimeout(() => setNow(Date.now()), Math.max(0, deadline - Date.now()) + 50);
    return () => clearTimeout(timer);
  }, [state, deadline]);

  const retry = () => {
    if (retrying) return;
    setRetrying(true);
    void resyncChatMessages(qc, message.chatId).finally(() => setRetrying(false));
  };

  return (
    <div className="space-y-0.5">
      {message.text ? (
        <div className="text-sm break-words whitespace-pre-wrap">
          <LinkifiedText text={message.text} />
        </div>
      ) : null}
      {state === 'unavailable' ? (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] opacity-80">
          <AlertCircle className="size-3 shrink-0" aria-hidden />
          <span className="italic">Mídia indisponível</span>
          <button
            type="button"
            onClick={retry}
            disabled={retrying}
            className="inline-flex items-center gap-1 rounded font-medium underline underline-offset-2 outline-none hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring/50 disabled:no-underline disabled:opacity-70"
          >
            {retrying ? <Spinner className="size-3" /> : null}
            {retrying ? 'Consultando…' : 'Tentar de novo'}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-[11px] italic opacity-70">
          <Spinner className="size-3 shrink-0" />
          baixando mídia…
        </div>
      )}
    </div>
  );
}

function MediaUnavailable() {
  return (
    <div className="flex items-center gap-1.5 text-xs italic opacity-80">
      <AlertCircle className="size-3.5 shrink-0" aria-hidden />
      Mídia indisponível
    </div>
  );
}

function MediaShimmer({ className }: { className?: string }) {
  return (
    <div
      aria-busy
      className={cn(
        'flex animate-pulse items-center justify-center rounded-md bg-current/10',
        className
      )}
    >
      <Spinner className="size-4 opacity-50" />
    </div>
  );
}

// ── Por tipo ────────────────────────────────────────────────────────────────

function MediaImage({ message }: { message: ChatMessage }) {
  const { url, loading, error } = useChatMedia(message.mediaUrl);
  // Presignada expirada/apagada entre o resolve e o load do <img>.
  const [broken, setBroken] = useState(false);
  if (loading) return <MediaShimmer className="h-40 w-52" />;
  if (error || broken || !url) return <MediaUnavailable />;
  return (
    <button
      type="button"
      onClick={() => window.open(url, '_blank', 'noopener')}
      title="Abrir em nova aba"
      className="block cursor-zoom-in overflow-hidden rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      <img
        src={url}
        alt={message.caption ?? message.mediaFilename ?? 'Imagem'}
        loading="lazy"
        onError={() => setBroken(true)}
        className="max-h-[280px] max-w-full rounded-md object-contain"
      />
    </button>
  );
}

function MediaAudio({ message }: { message: ChatMessage }) {
  const { url, loading, error } = useChatMedia(message.mediaUrl);
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  if (loading) return <MediaShimmer className="h-10 w-56" />;
  if (error || !url) return <MediaUnavailable />;
  // nota de voz não tem nome de arquivo: o player inline não oferece baixar
  // em todo navegador — o botão pede a versão ANEXO (audio-<id>.<ext>)
  const download = () => {
    if (!message.mediaUrl || downloading) return;
    setDownloading(true);
    setDownloadFailed(false);
    downloadChatMedia(message.mediaUrl)
      .catch(() => setDownloadFailed(true))
      .finally(() => setDownloading(false));
  };
  return (
    <div className="flex items-center gap-1.5">
      <audio controls src={url} preload="metadata" className="h-10 max-w-full" />
      <button
        type="button"
        onClick={download}
        disabled={downloading}
        title={downloadFailed ? 'Falha ao baixar — tente de novo' : 'Baixar áudio'}
        aria-label="Baixar áudio"
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-md outline-none transition-colors hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-ring/50',
          downloadFailed && 'text-destructive'
        )}
      >
        {downloading ? <Spinner className="size-4" /> : <Download className="size-4" />}
      </button>
    </div>
  );
}

function MediaVideo({ message }: { message: ChatMessage }) {
  const { url, loading, error } = useChatMedia(message.mediaUrl);
  if (loading) return <MediaShimmer className="h-40 w-56" />;
  if (error || !url) return <MediaUnavailable />;
  return (
    <video
      controls
      src={url}
      className="max-h-[280px] max-w-full rounded-md"
      preload="metadata"
    />
  );
}

/** Card de arquivo: ícone + nome + tamanho; o href é a própria presignada —
 *  navegação não sofre bloqueio CORS e o browser cuida do download. */
function MediaDocument({ message, inbound }: { message: ChatMessage; inbound: boolean }) {
  const { url, loading, error } = useChatMedia(message.mediaUrl);
  if (error) return <MediaUnavailable />;

  return (
    <a
      href={url ?? undefined}
      download={message.mediaFilename ?? undefined}
      target="_blank"
      rel="noopener noreferrer"
      title="Baixar arquivo"
      aria-disabled={url === null}
      className={cn(
        'flex w-full max-w-64 items-center gap-2.5 rounded-md px-2.5 py-2 text-left no-underline outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/50',
        inbound ? 'bg-muted hover:bg-muted/70' : 'bg-primary-foreground/10 hover:bg-primary-foreground/20',
        url === null && 'pointer-events-none opacity-70'
      )}
    >
      {loading ? (
        <Spinner className="size-4 shrink-0" />
      ) : (
        <FileText className="size-5 shrink-0 opacity-80" aria-hidden />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">
          {message.mediaFilename ?? 'Arquivo'}
        </span>
        <span className="block text-[11px] opacity-70">
          {message.mediaSize !== null ? formatFileSize(message.mediaSize) : 'Documento'}
        </span>
      </span>
    </a>
  );
}

// ── Entrada única usada pela bolha ──────────────────────────────────────────

export function MessageMedia({ message, inbound }: { message: ChatMessage; inbound: boolean }) {
  // Backfill em andamento (ou que falhou): fallback textual + dica, sem
  // duplicar a caption (o `text` do servidor já a carrega).
  if (message.mediaUrl === null) return <MediaPending message={message} />;

  let media: ReactNode;
  if (message.contentType === 'document') {
    media = <MediaDocument message={message} inbound={inbound} />;
  } else if (message.contentType === 'image' || message.contentType === 'sticker') {
    media = <MediaImage message={message} />;
  } else if (message.contentType === 'audio') {
    media = <MediaAudio message={message} />;
  } else if (message.contentType === 'video') {
    media = <MediaVideo message={message} />;
  } else {
    media = <MediaUnavailable />;
  }

  return (
    <div className="space-y-1">
      {media}
      {message.caption ? (
        <div className="text-sm break-words whitespace-pre-wrap">
          <LinkifiedText text={message.caption} />
        </div>
      ) : null}
    </div>
  );
}
